import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useContactNpoRefund } from '@/api/hooks/donationHooks';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { ContactNpoData, contactNpoResolver } from '@/lib/validations/ContactNpoSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';
import Text from '../Text';

interface ContactNpoFormProps {
  transactionId: string;
  npoName: string;
  onDone: () => void;
}

const ContactNpoForm = ({ transactionId, npoName, onDone }: ContactNpoFormProps) => {
  const { mutateAsync: contactNpo, isPending } = useContactNpoRefund();
  const { showSuccess } = useEndFlowModal();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors }
  } = useForm<ContactNpoData>({
    mode: 'onChange',
    resolver: contactNpoResolver
  });

  const onSubmit: SubmitHandler<ContactNpoData> = async (data) => {
    contactNpo(
      {
        transactionId,
        email: data.email.toLocaleLowerCase(),
        reason: data.reason
      },
      {
        onSuccess: () => {
          showSuccess({
            title: i18n.t('end_flow.success_title'),
            message: i18n.t('settings.transactions.contacted_npo', { NPO_NAME: npoName })
          });
        },
        onSettled: () => {
          onDone();
        }
      }
    );
  };

  return (
    <View style={style.formContainer}>
      <TextInputField type="email-address" name="email" control={control} placeholder={i18n.t('mix.email')} />

      <TextInputField
        name="reason"
        control={control}
        placeholder={i18n.t('mix.message')}
        multiline
        minHeight={120}
        maxHeight={120}
      />

      {errors.root && <Text style={style.errorText}>{errors?.root?.message}</Text>}
      <View style={style.buttonContainer}>
        <BrandedButton loading={isPending} style={style.btnStyle} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {i18n.t('mix.submit')}
        </BrandedButton>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  formContainer: {
    width: '100%',
    flexDirection: 'column',
    gap: 28
  },

  buttonContainer: {
    justifyContent: 'flex-end'
  },

  btnStyle: {
    width: '100%'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default ContactNpoForm;
